import { BalanceContext } from '@app/collections/fortress/BalanceContext'
import { Col, Divider, Row, Spacer, Text } from '@app/components'
import { PULSECHAIN_ID } from '@app/config/env'
import { IStore } from '@app/redux'
import { auth, Axios } from '@app/services'
import { toCurrency } from '@app/utils'
import { ETHEREUM_MAINNET_ID, useWalletChainId } from 'libs/ethereum/useWalletChainId'
import useCSTBalance, {
  useEhexBalance,
  useGloryBalance,
  useHexBalance,
  useIncBalance,
  usePhlpBalance,
  usePlsBalance,
  usePlsxBalance,
  //PRVX change here
  usePrvxBalance,
  useTideBalance,
  useWaitBalance,
} from 'libs/services/hooks/useCSTBalance'
import { shouldDebugBalanceReads } from 'libs/services/hooks/useRead'
import React, { useContext, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useAccount } from 'wagmi'

import { CoastIcon, HexIcon, PulseChainIcon, PulseXIcon } from '~public'
//import { useMintFee } from 'libs/services/hooks/useFee'
import { GloryIcon } from '~public/icons/GloryIcon'
import { IncIcon } from '~public/icons/IncIcon'
import { PHLPv2Icon } from '~public/icons/PHLPv2Icon'
//PRVX change here
import { ProveXIcon } from '~public/icons/ProveXIcon'
import { TideIcon } from '~public/icons/TideIcon'

import { UserInfoContext } from '../fortress/UserInfoContext'
import { CurrencyInput } from './currency-input'
import { Summary } from './summary'
import { Transaction } from './transactions/Transactions'

export const PiteasBuy: React.FC = () => {
  const { user } = useSelector((state: IStore) => state)

  const { balance, fetchAccounts } = useContext(BalanceContext)
  const { fetchUserInfo } = useContext(UserInfoContext)
  const [selectedWallet] = useState<any>({ name: '', val: '' })
  const [selectedMethod] = useState<any>({ name: '', val: '' })
  const [cst, setCst] = useState(0)
  //const [coastBalance, setCoastBalance] = useState(0)
  const fee = 1 //useMintFee()

  // Input states (Pay and Receive)
  const { address } = useAccount()
  const chainId = useWalletChainId()
  const onEthereum = chainId === ETHEREUM_MAINNET_ID

  const [payAmount, setPayAmount] = useState(0)
  const [payType, setPayType] = useState('USD')

  const [receiveAmount, setReceiveAmount] = useState(0)
  const [receiveType, setReceiveType] = useState(onEthereum ? 'eHEX' : 'PLS')
  const [transactionRefresh, setTransactionRefresh] = useState(0)

  const PlsBalance = usePlsBalance()
  const PlsxBalance = usePlsxBalance(transactionRefresh)
  const HexBalance = useHexBalance(transactionRefresh)
  const WaitBalance = useWaitBalance(transactionRefresh)
  const IncBalance = useIncBalance(transactionRefresh)
  const PhlpBalance = usePhlpBalance(transactionRefresh)
  const TideBalance = useTideBalance(transactionRefresh)
  const GloryBalance = useGloryBalance(transactionRefresh)
  //PRVX change here
  const PrvxBalance = usePrvxBalance(transactionRefresh)
  const CSTBalance = useCSTBalance(transactionRefresh)
  const EhexBalance = useEhexBalance(transactionRefresh)
  const [queueLength, setQueueLength] = useState(0)

  useEffect(() => {
    if (onEthereum) {
      setReceiveType('eHEX')
      setReceiveAmount(0)
    } else if (receiveType === 'eHEX') {
      setReceiveType('PLS')
      setReceiveAmount(0)
    }
  }, [onEthereum])

  useEffect(() => {
    if (!shouldDebugBalanceReads()) return
    const pulseFromEnv = parseInt(process.env.NEXT_PUBLIC_PULSECHAIN ?? '0', 10)
    // eslint-disable-next-line no-console
    console.log('[PiteasBuy chain + ContractRead snapshot]', {
      address,
      chainId,
      chainIdType: typeof chainId,
      pulseFromEnv,
      PULSECHAIN_ID,
      strictEqualToPULSECHAIN_ID: chainId === PULSECHAIN_ID,
      matchesUiGate: chainId === pulseFromEnv,
      pls: { formatted: PlsBalance.value, isLoading: PlsBalance.isLoading },
      plsx: { state: PlsxBalance.state, value: PlsxBalance.value?.toString?.() ?? PlsxBalance.value },
      hex: { state: HexBalance.state, value: HexBalance.value?.toString?.() ?? HexBalance.value },
      wait: { state: WaitBalance.state, value: WaitBalance.value?.toString?.() ?? WaitBalance.value },
      inc: { state: IncBalance.state, value: IncBalance.value?.toString?.() ?? IncBalance.value },
      phlp: { state: PhlpBalance.state, value: PhlpBalance.value?.toString?.() ?? PhlpBalance.value },
      tide: { state: TideBalance.state, value: TideBalance.value?.toString?.() ?? TideBalance.value },
      glory: { state: GloryBalance.state, value: GloryBalance.value?.toString?.() ?? GloryBalance.value },
      prvx: { state: PrvxBalance.state, value: PrvxBalance.value?.toString?.() ?? PrvxBalance.value },
      cst: { state: CSTBalance.state, value: CSTBalance.value?.toString?.() ?? CSTBalance.value },
    })
  }, [
    address,
    chainId,
    transactionRefresh,
    PlsBalance.value,
    PlsBalance.isLoading,
    PlsxBalance.state,
    PlsxBalance.value,
    HexBalance.state,
    HexBalance.value,
    WaitBalance.state,
    WaitBalance.value,
    IncBalance.state,
    IncBalance.value,
    PhlpBalance.state,
    PhlpBalance.value,
    TideBalance.state,
    TideBalance.value,
    GloryBalance.state,
    GloryBalance.value,
    PrvxBalance.state,
    PrvxBalance.value,
    CSTBalance.state,
    CSTBalance.value,
  ])

  useEffect(() => {
    fetchAccounts()
    fetchUserInfo()
  }, [transactionRefresh])

  async function fetchQueueLength() {
    const authorization = await auth.currentUser.getIdToken()
    const params = { identityId: user.fortressIdentityId }
    const headers = {
      Authorization: authorization,
    }

    const res = await Axios.get('api/gcloud/queues', { params, headers })

    // eslint-disable-next-line no-console
    console.log('Number of tasks in the queue:', res.data.length)
    setQueueLength(res.data.length)
  }

  useEffect(() => {
    if (onEthereum) return
    fetchQueueLength()
  }, [transactionRefresh, receiveAmount, onEthereum])

  const setAmount = (value: number, title: string) => {
    //setType(title)
    if (selectedMethod.name == 'Digital Account') {
      setPayAmount(value ? value : 0)
      setReceiveAmount(value ? value : 0)
    } else if (title == 'You Pay') {
      if (value == 0 || !value) {
        setPayAmount(0)
        setReceiveAmount(0)
      } else {
        setPayAmount(value)
      }
      //Do not change this line of code
      //setReceiveAmount(value ? round(value * (1 - fee / 100), 2) : 0)
    }
  }

  return (
    <>
      <Row justify="space-between">
        <Col span={24} lg={12} sm={24}>
          <CurrencyInput
            pay={{ amount: payAmount, setAmount: setPayAmount, type: payType, setType: setPayType }}
            receive={{
              amount: receiveAmount,
              setAmount: setReceiveAmount,
              type: receiveType,
              setType: setReceiveType,
            }}
            setAmount={setAmount}
            balance={balance}
          />
          <Spacer value={40} />

          <Row style={{ rowGap: '45px', columnGap: '45px' }}>
            <Col span={24}>
              <Row justify={'space-between'}>
                <Col style={{ marginBottom: 10 }}>
                  <Text>Connected Wallet</Text>
                </Col>
              </Row>
              <Divider style={{ marginTop: 0 }} />
              {address != undefined && onEthereum ? (
                <Row gutter={[10, 10]}>
                  <Col>
                    <HexIcon style={{ height: 55, width: 55 }} />
                  </Col>
                  <Col>
                    <Text>Ethereum HEX</Text>
                    <Spacer value={1} />
                    <Text type="secondary">{toCurrency(Number(EhexBalance.value || 0) / 10 ** 8, false)} eHEX</Text>
                  </Col>
                </Row>
              ) : address != undefined && chainId == parseInt(process.env.NEXT_PUBLIC_PULSECHAIN) ? (
                <>
                  <Row gutter={[10, 10]}>
                    <Col span={8} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      {/* Pulse */}
                      <Col style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <PulseChainIcon style={{ height: 70, width: 70, marginBottom: '0px' }} />
                        <Text type="secondary">
                          <>{address != undefined ? <>{toCurrency(Number(PlsBalance.value), false)}</> : '0'}</>
                        </Text>
                      </Col>
                    </Col>
                    <Col span={8} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      {/* PulseX */}
                      <Col style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <PulseXIcon style={{ height: 50, width: 50, marginBottom: '5px' }} />
                        <Text type="secondary">
                          <>
                            {address != undefined ? (
                              <>{toCurrency(Number(PlsxBalance.value) / 10 ** 18, false)}</>
                            ) : (
                              '0'
                            )}
                          </>
                        </Text>
                      </Col>
                    </Col>
                    <Col span={8} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      {/* HEX */}
                      <Col style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <HexIcon style={{ height: 50, width: 50, marginLeft: '5px', marginBottom: '5px' }} />
                        <Text type="secondary" style={{ marginLeft: '5px' }}>
                          <>
                            {address != undefined ? <>{toCurrency(Number(HexBalance.value) / 10 ** 8, false)}</> : '0'}
                          </>
                        </Text>
                      </Col>
                    </Col>
                  </Row>
                  <Spacer value={30} />
                  <Row gutter={[10, 10]}>
                    <Col span={8} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      {/* INC */}
                      <Col style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <IncIcon style={{ height: 50, width: 50, marginLeft: '5px', marginBottom: '5px' }} />
                        <Text type="secondary" style={{ marginLeft: '5px' }}>
                          <>
                            {address != undefined ? <>{toCurrency(Number(IncBalance.value) / 10 ** 18, false)}</> : '0'}
                          </>
                        </Text>
                      </Col>
                    </Col>
                    <Col span={8} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      {/* PHLPv2 */}
                      <Col style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <PHLPv2Icon style={{ height: 50, width: 50, marginLeft: '5px', marginBottom: '5px' }} />
                        <Text type="secondary" style={{ marginLeft: '5px' }}>
                          <>
                            {address != undefined ? (
                              <>{toCurrency(Number(PhlpBalance.value) / 10 ** 18, false)}</>
                            ) : (
                              '0'
                            )}
                          </>
                        </Text>
                      </Col>
                    </Col>
                    <Col span={8} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      {/* CST */}
                      <Col style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <CoastIcon style={{ height: 50, width: 50, marginBottom: '5px' }} />
                        <Text type="secondary">
                          <>
                            {address != undefined ? <>{toCurrency(Number(CSTBalance.value) / 10 ** 6, false)}</> : '0'}
                          </>
                        </Text>
                      </Col>
                    </Col>
                  </Row>
                  <Spacer value={30} />
                  <Row gutter={[10, 10]}>
                    <Col span={8} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      {/* PRVX */}
                      <Col style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <ProveXIcon style={{ height: 50, width: 50, marginLeft: '5px', marginBottom: '5px' }} />
                        <Text type="secondary" style={{ marginLeft: '5px' }}>
                          <>
                            {address != undefined ? (
                              <>{toCurrency(Number(PrvxBalance.value) / 10 ** 18, false)}</>
                            ) : (
                              '0'
                            )}
                          </>
                        </Text>
                      </Col>
                    </Col>
                    <Col span={8} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      {/* GLORY */}
                      <Col style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <GloryIcon style={{ height: 50, width: 50, marginLeft: '5px', marginBottom: '5px' }} />
                        <Text type="secondary" style={{ marginLeft: '5px' }}>
                          <>
                            {address != undefined ? (
                              <>{toCurrency(Number(GloryBalance.value) / 10 ** 6, false)}</>
                            ) : (
                              '0'
                            )}
                          </>
                        </Text>
                      </Col>
                    </Col>
                    <Col span={8} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      {/* TIDE */}
                      <Col style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <TideIcon style={{ height: 50, width: 50, marginLeft: '5px', marginBottom: '5px' }} />
                        <Text type="secondary" style={{ marginLeft: '5px' }}>
                          <>
                            {address != undefined ? (
                              <>{toCurrency(Number(TideBalance.value) / 10 ** 18, false)}</>
                            ) : (
                              '0'
                            )}
                          </>
                        </Text>
                      </Col>
                    </Col>
                  </Row>
                </>
              ) : (
                <Row gutter={[10, 10]}>
                  <Col>
                    <CoastIcon style={{ height: 55, width: 55 }} />
                  </Col>
                  <Col>
                    <Text>Current Balance</Text>
                    <Spacer value={1} />
                    <Text type="secondary">Connect wallet</Text>
                  </Col>
                </Row>
              )}
            </Col>

            <Spacer />
          </Row>
        </Col>

        {/* Summary ############################## */}
        <Col span={24} sm={24} lg={12} style={{ marginTop: 31 }}>
          <Summary
            method={selectedMethod}
            wallet={selectedWallet}
            purchaseData={{
              pay: { amount: payAmount, setAmount: setPayAmount, type: payType, setType: setPayType },
              receive: {
                amount: receiveAmount,
                setAmount: setReceiveAmount,
                type: receiveType,
                setType: setReceiveType,
              },
            }}
            refresh={fetchAccounts}
            fee={fee}
            cst={cst}
            setCst={setCst}
            balance={balance}
            transactionRefresh={transactionRefresh}
            setTransactionRefresh={setTransactionRefresh}
            queueLength={queueLength}
          />
        </Col>

        <Col span={24}>
          <Transaction transactionRefresh={transactionRefresh} />
        </Col>
      </Row>
    </>
  )
}
